/**
 * RFC 0345 3c: the owner's question about a document that may reach a shared
 * session. One card per document, above the composer like a question; the
 * agent is not waiting on it. The candidate comes from the transcript, which
 * guests can edit, so the card does not claim the agent wrote it. Approve is
 * the only choice until Always allow has the 3c trust prerequisite.
 */

import type {
  ApprovalChoice,
  PendingDocument,
} from '../../services/sharing/documentApprovals'

export function DocumentApprovals({
  pending,
  onChoose,
  onOpenDocument,
  error,
}: {
  pending: PendingDocument[]
  onChoose: (documentId: string, choice: ApprovalChoice) => void
  onOpenDocument?: (documentId: string) => void
  error?: string
}) {
  if (!pending.length) return null
  return (
    <section
      className="mx-auto w-full max-w-3xl space-y-2 px-3 pb-2"
      aria-label="Documents to share"
    >
      {pending.map((doc) => (
        <div
          key={doc.documentId}
          className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm"
        >
          <p>
            Share{' '}
            {onOpenDocument ? (
              <button
                type="button"
                className="font-medium underline-offset-2 hover:underline"
                onClick={() => onOpenDocument(doc.documentId)}
              >
                {doc.title}
              </button>
            ) : (
              <span className="font-medium">{doc.title}</span>
            )}{' '}
            with this session?
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Choice onClick={() => onChoose(doc.documentId, 'once')}>
              Approve
            </Choice>
          </div>
        </div>
      ))}
      {error ? <p className="text-destructive text-xs">{error}</p> : null}
    </section>
  )
}

function Choice({
  onClick,
  children,
}: {
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-border px-2.5 py-0.5 text-xs hover:bg-muted"
    >
      {children}
    </button>
  )
}
